import { diffLines, type Change } from "diff";

export type DiffGroup = {
    type: "added" | "removed" | "unchanged";
    lines: string[];
};

export type DiffStats = {
    additions: number;
    deletions: number;
};

export const calculateDiff = (oldValue: string, newValue: string): DiffGroup[] => {
    // Handle empty strings case
    if (oldValue === "" && newValue === "") {
        return [];
    }

    // Handle cases where one value is empty
    if (oldValue === "") {
        const lines = newValue.split("\n");
        // Remove only the final empty line if it exists (trailing newline)
        if (lines.length > 0 && lines[lines.length - 1] === "") {
            lines.pop();
        }
        return [
            {
                type: "added",
                lines,
            },
        ];
    }

    if (newValue === "") {
        const lines = oldValue.split("\n");
        // Remove only the final empty line if it exists (trailing newline)
        if (lines.length > 0 && lines[lines.length - 1] === "") {
            lines.pop();
        }
        return [
            {
                type: "removed",
                lines,
            },
        ];
    }

    // Normalize text to ensure consistent line endings for proper diffing
    const normalizeText = (text: string) => (text.endsWith("\n") ? text : text + "\n");

    const changes: Change[] = diffLines(normalizeText(oldValue), normalizeText(newValue), {
        ignoreWhitespace: false,
    });

    const groups: DiffGroup[] = [];

    for (const change of changes) {
        const content = change.value;
        const type = change.added === true ? "added" : change.removed === true ? "removed" : "unchanged";

        // Split content into lines and filter out empty trailing lines
        const lines = content.split("\n").filter((line: string, index: number, array: string[]) => {
            // Keep all lines except the last one if it's empty (trailing newline)
            if (index === array.length - 1 && line === "") {
                return false;
            }
            return true;
        });

        if (lines.length > 0) {
            // Try to merge with the previous group if it's the same type
            if (groups.length > 0 && groups[groups.length - 1].type === type) {
                groups[groups.length - 1].lines.push(...lines);
            } else {
                groups.push({ type, lines });
            }
        }
    }

    return groups;
};

export const calculateDiffStats = (diffGroups: DiffGroup[]): DiffStats => {
    let additions = 0;
    let deletions = 0;

    for (const group of diffGroups) {
        if (group.type === "added") {
            additions += group.lines.length;
        } else if (group.type === "removed") {
            deletions += group.lines.length;
        }
    }

    return { additions, deletions };
};
